import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from './supabaseClient'

const LOG_PREFIX = '[admin-manage-user-client]'

const buildSessionError = (message, code = 'SESSION_EXPIRED') => {
  const err = new Error(message)
  err.code = code
  return err
}

const decodeJwtPayload = (token) => {
  try {
    const parts = String(token || '').split('.')
    if (parts.length !== 3) return null
    const payloadB64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(parts[1].length / 4) * 4, '=')
    return JSON.parse(atob(payloadB64))
  } catch {
    return null
  }
}

const summarizeJwtToken = (token) => {
  const raw = String(token || '')
  const parts = raw.split('.')
  const payload = decodeJwtPayload(raw)
  return {
    validShape: parts.length === 3 && !!payload,
    partCount: parts.length,
    ref: payload?.ref || null,
    iss: payload?.iss || null,
    aud: payload?.aud || null,
    role: payload?.role || null,
    sub: payload?.sub || null,
    exp: payload?.exp || null,
  }
}

export async function getValidSessionAccessToken() {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
  if (sessionError) {
    console.error(`${LOG_PREFIX} sem sessão`, sessionError)
    throw buildSessionError('Sessão expirada. Faça login novamente.')
  }

  let session = sessionData?.session || null
  if (!session?.user?.id) {
    console.warn(`${LOG_PREFIX} sem sessão ativa`)
    throw buildSessionError('Sessão expirada. Faça login novamente.')
  }

  let token = String(session?.access_token || '').trim()
  if (!token) {
    console.warn(`${LOG_PREFIX} sem token`)
    throw buildSessionError('Sessão expirada. Faça login novamente.')
  }

  // Força o SDK a validar/renovar a sessão antes de usar o token em Edge Function.
  const { data: userData, error: userError } = await supabase.auth.getUser()
  if (userError || !userData?.user?.id) {
    console.warn(`${LOG_PREFIX} getUser falhou; tentando refresh`, userError)
    const refreshedToken = await refreshAccessToken()
    if (!refreshedToken) {
      throw buildSessionError('Sessão expirada. Faça login novamente.', 'INVALID_TOKEN')
    }
    const { data: refreshedSessionData } = await supabase.auth.getSession()
    session = refreshedSessionData?.session || null
    token = String(session?.access_token || refreshedToken || '').trim()
  } else {
    const { data: latestSessionData } = await supabase.auth.getSession()
    session = latestSessionData?.session || session
    token = String(session?.access_token || token).trim()
  }

  if (!token) {
    console.warn(`${LOG_PREFIX} token ausente após validação/refresh`)
    throw buildSessionError('Sessão expirada. Faça login novamente.', 'INVALID_TOKEN')
  }

  const payload = decodeJwtPayload(token)
  if (!payload) {
    console.warn(`${LOG_PREFIX} token sem payload decodificável; validação final ficará no backend`)
    return token
  }

  const exp = Number(payload?.exp || 0)
  if (exp > 0 && Date.now() >= exp * 1000) {
    console.warn(`${LOG_PREFIX} token expirado`)
    throw buildSessionError('Sessão expirada. Faça login novamente.', 'INVALID_TOKEN')
  }

  return token
}

async function refreshAccessToken() {
  try {
    const { data, error } = await supabase.auth.refreshSession()
    if (error) {
      console.warn(`${LOG_PREFIX} falha ao renovar sessão`, error)
      return null
    }
    const nextToken = String(data?.session?.access_token || '').trim()
    return nextToken || null
  } catch (err) {
    console.warn(`${LOG_PREFIX} exceção ao renovar sessão`, err)
    return null
  }
}

function buildEdgeError(status, parsed, rawText, extra = {}) {
  return {
    message:
      parsed?.error ||
      parsed?.message ||
      rawText ||
      `Edge Function returned status ${status}`,
    context: {
      status,
      json: async () => parsed,
      text: async () => rawText,
    },
    ...extra,
  }
}

async function invokeWithToken(token, { action, payload }) {
  const clientTokenInfo = summarizeJwtToken(token)
  try {
    const { data, error } = await supabase.functions.invoke('admin-manage-user', {
      body: { action, payload },
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
      },
    })

    if (error) {
      const status = Number(error?.context?.status || error?.status || 500)
      let parsed = null
      let rawText = ''
      try {
        parsed = await error.context?.json?.()
      } catch {
        parsed = null
      }
      try {
        rawText = await error.context?.text?.()
      } catch {
        rawText = ''
      }
      return { data: null, error: buildEdgeError(status, parsed, rawText, { clientTokenInfo }) }
    }

    return { data: data ?? null, error: null }
  } catch (err) {
    const status = Number(err?.status || err?.context?.status || 500)
    const rawText = String(err?.message || '')
    const parsed = err?.data || null
    return { data: null, error: buildEdgeError(status, parsed, rawText, { clientTokenInfo }) }
  }
}

export async function invokeAdminManageUser({ action, payload }) {
  // Usa fetch direto para evitar qualquer header Authorization duplicado do SDK.
  let token = await getValidSessionAccessToken()
  let result = await invokeWithToken(token, { action, payload })
  if (result?.error?.context?.status !== 401) return result

  // Retry único após refresh.
  console.warn(`${LOG_PREFIX} token rejeitado; tentando renovar sessão e reenviar`)
  const refreshedToken = await refreshAccessToken()
  if (refreshedToken && refreshedToken !== token) {
    token = refreshedToken
    result = await invokeWithToken(token, { action, payload })
  }

  return result
}
